/**
 * @voltxampere/engine — B-source expression DSL parser (Sprint 54.2b-4)
 *
 * Recursive-descent parser over the Token stream from `./lexer.ts`.
 * Grammar in `./ast.ts` docstring. No backtracking, linear-time.
 *
 * Optional `IF` keyword is sugar: `IF (a) ? b : c` parses identically
 * to `(a) ? b : c`. ngspice accepts both forms.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
class TokenStream {
    tokens;
    i = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    peek(offset = 0) {
        const t = this.tokens[this.i + offset];
        if (!t) {
            throw new ContractViolationError("dsl/parser: unexpected end of token stream");
        }
        return t;
    }
    consume() {
        const t = this.peek();
        this.i++;
        return t;
    }
    expect(kind, valueHint) {
        const t = this.peek();
        if (t.kind !== kind || (valueHint !== undefined && t.value !== valueHint)) {
            throw new ContractViolationError(`dsl/parser: expected ${kind}${valueHint ? ` '${valueHint}'` : ""} at position ${String(t.position)}, got ${t.kind} '${t.value}'`);
        }
        return this.consume();
    }
    atEnd() {
        return this.peek().kind === "EOF";
    }
}
/**
 * Parse a B-source expression source string into an Expr AST.
 * Throws ContractViolationError on syntax error; the message
 * includes character position so the engine can surface a useful
 * error in `DCResult.warnings`.
 */
export function parseExpr(tokens) {
    const stream = new TokenStream(tokens);
    const expr = parseConditional(stream);
    if (!stream.atEnd()) {
        const t = stream.peek();
        throw new ContractViolationError(`dsl/parser: trailing tokens after expression — ${t.kind} '${t.value}' at position ${String(t.position)}`);
    }
    return expr;
}
/**
 * conditional := comparison ('?' conditional ':' conditional)?
 *              | 'if' '(' comparison ')' '?' conditional ':' conditional
 *
 * The optional `IF` keyword is consumed if present; the ternary
 * syntax is otherwise identical.
 */
function parseConditional(stream) {
    // Optional IF keyword. ngspice form: `IF (test) ? consequent : alternate`.
    // After IF the test must NOT be a full conditional (otherwise
    // `IF (...)?x:y` would gobble the `?x:y` into the test). Parse
    // only the comparison/arithmetic — parenthesised inner expr can
    // still contain ternaries via the recursive LPAREN handling in
    // parsePrimary.
    const first = stream.peek();
    if (first.kind === "IDENT" && first.value === "if") {
        stream.consume();
        const test = parseComparison(stream);
        stream.expect("QMARK");
        const consequent = parseConditional(stream);
        stream.expect("COLON");
        const alternate = parseConditional(stream);
        return { kind: "conditional", test, consequent, alternate };
    }
    const cond = parseComparison(stream);
    if (stream.peek().kind === "QMARK") {
        stream.consume();
        const consequent = parseConditional(stream);
        stream.expect("COLON");
        const alternate = parseConditional(stream);
        return { kind: "conditional", test: cond, consequent, alternate };
    }
    return cond;
}
/**
 * comparison := arithmetic (CMP arithmetic)?
 *
 * At most one comparison in a chain — `a < b < c` is rejected.
 * The parser does not parenthesise associativity for comparisons
 * because B-source predicates are single-step ("V(in) > 0.5").
 */
function parseComparison(stream) {
    const left = parseArithmetic(stream);
    if (stream.peek().kind === "CMP") {
        const tok = stream.consume();
        const op = tok.value;
        const right = parseArithmetic(stream);
        return { kind: "comparison", op, left, right };
    }
    return left;
}
/**
 * arithmetic := term (('+' | '-') term)*
 */
function parseArithmetic(stream) {
    let left = parseTerm(stream);
    for (;;) {
        const t = stream.peek();
        if (t.kind !== "OP" || (t.value !== "+" && t.value !== "-"))
            break;
        stream.consume();
        const right = parseTerm(stream);
        left = { kind: "binary", op: t.value, left, right };
    }
    return left;
}
/**
 * term := unary (('*' | '/') unary)*
 */
function parseTerm(stream) {
    let left = parseUnary(stream);
    for (;;) {
        const t = stream.peek();
        if (t.kind !== "OP" || (t.value !== "*" && t.value !== "/"))
            break;
        stream.consume();
        const right = parseUnary(stream);
        left = { kind: "binary", op: t.value, left, right };
    }
    return left;
}
/**
 * unary := ('-' | '+')? primary
 */
function parseUnary(stream) {
    const t = stream.peek();
    if (t.kind === "OP" && t.value === "-") {
        stream.consume();
        const operand = parsePrimary(stream);
        return { kind: "negate", operand };
    }
    if (t.kind === "OP" && t.value === "+") {
        stream.consume();
        return parsePrimary(stream);
    }
    return parsePrimary(stream);
}
/**
 * primary := NUMBER | IDENT '(' args? ')' | LPAREN expr RPAREN
 */
function parsePrimary(stream) {
    const t = stream.peek();
    if (t.kind === "NUMBER") {
        stream.consume();
        const value = parseFloat(t.value);
        if (!Number.isFinite(value)) {
            throw new ContractViolationError(`dsl/parser: non-finite number literal '${t.value}' at position ${String(t.position)}`);
        }
        return { kind: "literal", value };
    }
    if (t.kind === "LPAREN") {
        stream.consume();
        const inner = parseConditional(stream);
        stream.expect("RPAREN");
        return inner;
    }
    if (t.kind === "IDENT") {
        stream.consume();
        // V(net) and I(component) are reserved 1-arg forms with an
        // IDENT argument (not a parsed expr).
        if (stream.peek().kind === "LPAREN") {
            stream.consume();
            // V(net) / I(comp) take a bare identifier argument.
            if (t.value === "V" || t.value === "I") {
                const argTok = stream.expect("IDENT");
                stream.expect("RPAREN");
                if (t.value === "V") {
                    return { kind: "nodeRef", net: argTok.value };
                }
                return { kind: "componentRef", id: argTok.value };
            }
            // General function call — comma-separated expressions.
            const args = [];
            if (stream.peek().kind !== "RPAREN") {
                args.push(parseConditional(stream));
                while (stream.peek().kind === "COMMA") {
                    stream.consume();
                    args.push(parseConditional(stream));
                }
            }
            stream.expect("RPAREN");
            return { kind: "functionCall", name: t.value, args };
        }
        throw new ContractViolationError(`dsl/parser: bare identifier '${t.value}' at position ${String(t.position)} — expected function call or V()/I() reference`);
    }
    throw new ContractViolationError(`dsl/parser: unexpected ${t.kind} '${t.value}' at position ${String(t.position)}`);
}
//# sourceMappingURL=parser.js.map